'use client';

import * as React from 'react';
import * as RechartsPrimitive from 'recharts';
import type { TooltipValueType } from 'recharts';

import { cn } from '@/hen-ui/utils';

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: '', dark: '.dark' } as const;

const INITIAL_DIMENSION = { width: 320, height: 200 } as const;
type TooltipNameType = number | string;

export type ChartConfig = Record<
  string,
  {
    label?: React.ReactNode;
    icon?: React.ComponentType;
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  )
>;

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);

  if (!context) {
    throw new Error(
      'useChart must be used within <ChartContainer /> from @/hen-ui/shadcn/chart (Recharts context wrapper; not ChartFrame)',
    );
  }

  return context;
}

function ChartContainer({
  id,
  className,
  children,
  config,
  initialDimension = INITIAL_DIMENSION,
  ...props
}: React.ComponentProps<'div'> & {
  config: ChartConfig;
  children: React.ComponentProps<
    typeof RechartsPrimitive.ResponsiveContainer
  >['children'];
  initialDimension?: {
    width: number;
    height: number;
  };
}) {
  const uniqueId = React.useId();
  const chartId = `chart-${id ?? uniqueId.replace(/:/g, '')}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-slot='chart'
        data-chart={chartId}
        className={cn(
          "flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-neutral-500 [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-neutral-200/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-neutral-200 [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-neutral-200 [&_.recharts-radial-bar-background-sector]:fill-neutral-100 [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-neutral-100 [&_.recharts-reference-line_[stroke='#ccc']]:stroke-neutral-200 [&_.recharts-sector]:outline-none [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-none",
          className,
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer
          initialDimension={initialDimension}
        >
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
}

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(
    ([, config]) => config.theme ?? config.color,
  );

  if (!colorConfig.length) {
    return null;
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color =
      itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ??
      itemConfig.color;
    return color ? `  --color-${key}: ${color};` : null;
  })
  .join('\n')}
}
`,
          )
          .join('\n'),
      }}
    />
  );
};

const ChartTooltip = RechartsPrimitive.Tooltip;

const CHART_TOOLTIP_LABEL_WRAPPER_CLASS =
  'min-w-0 break-words text-left font-medium' as const;

function ChartTooltipContent({
  active,
  payload,
  className,
  indicator = 'dot',
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  valueFormatter,
  rowValueFormatter,
  color,
  nameKey,
  labelKey,
  footer,
  getItemIndicatorColor,
  borderColor,
}: React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
  React.ComponentProps<'div'> & {
    hideLabel?: boolean;
    hideIndicator?: boolean;
    indicator?: 'line' | 'dot' | 'dashed';
    nameKey?: string;
    labelKey?: string;
    /**
     * Override the tooltip bubble's outline with a custom color. When set,
     * the default 1px `neutral-200/50` border is replaced with a 2px solid
     * border in this color — used by `ScatterChart` to carry the active
     * series color on the bubble itself instead of in per-row indicator
     * swatches (which are redundant when every row belongs to the same
     * point/series). Other chart composites can opt in for the same effect.
     *
     * When omitted, the existing neutral border is preserved (no visual
     * change for current callers).
     */
    borderColor?: string;
    /**
     * Formatter for the numeric value slot only — keeps the default row
     * layout intact and just replaces what `toLocaleString()` would emit.
     * Prefer this over the full `formatter` prop when all you need is
     * unit-aware number formatting (percentages, currency, compact).
     *
     * Receives the series name so multi-series charts with heterogeneous
     * units can switch formatting per series. Non-numeric values bypass
     * this formatter (fall back to String(value)).
     */
    valueFormatter?: (value: number, name: string | number) => string;
    /**
     * Row-aware value formatter. Like `valueFormatter` but also receives
     * the full raw data row as its third argument, enabling sibling-column
     * lookups (e.g. reading `row.yoy_pct` to inject a YoY % delta).
     *
     * Takes precedence over `valueFormatter`. Only replaces the value
     * cell — the indicator dot and series label remain visible.
     *
     * Any exception thrown by this function is caught; the raw value is
     * shown instead and a `console.error` is emitted.
     */
    rowValueFormatter?: (
      value: number,
      name: string,
      row: Record<string, unknown>,
    ) => React.ReactNode;
    /**
     * Optional methodology copy rendered as a footer note at the bottom of
     * the tooltip bubble. Accepts a pre-rendered `React.ReactNode` so
     * callers control markup (use `renderTooltipFooter` from
     * `chart-utils` to produce a node from a `ChartTooltipFooter` value).
     */
    footer?: React.ReactNode;
    /**
     * Per-item indicator-color resolver. Called with each tooltip payload
     * item; when it returns a string, that color overrides the default
     * indicator color (which is the series-level `Bar.fill` / `Line.stroke`
     * baked into `item.color`).
     *
     * Composites use this to mirror semantic / per-point `colorRules`
     * colors into the tooltip indicator dot — without it the indicator
     * shows the base series color even when the bar/dot on-chart renders
     * in a different per-point color.
     */
    getItemIndicatorColor?: (item: {
      dataKey?: string | number | ((obj: unknown) => unknown);
      name?: TooltipNameType;
      value?: TooltipValueType;
      color?: string;
      payload?: Record<string, unknown>;
    }) => string | undefined;
  } & Omit<
    RechartsPrimitive.DefaultTooltipContentProps<
      TooltipValueType,
      TooltipNameType
    >,
    'accessibilityLayer'
  >) {
  const { config } = useChart();

  const tooltipLabel = React.useMemo(() => {
    if (hideLabel || !payload?.length) {
      return null;
    }

    const [item] = payload;
    const key = `${labelKey ?? item?.dataKey ?? item?.name ?? 'value'}`;
    const itemConfig = getPayloadConfigFromPayload(config, item, key);
    const value =
      !labelKey && typeof label === 'string'
        ? (config[label]?.label ?? label)
        : itemConfig?.label;

    if (labelFormatter) {
      return (
        <div className={cn(CHART_TOOLTIP_LABEL_WRAPPER_CLASS, labelClassName)}>
          {labelFormatter(value, payload)}
        </div>
      );
    }

    if (!value) {
      return null;
    }

    return (
      <div className={cn(CHART_TOOLTIP_LABEL_WRAPPER_CLASS, labelClassName)}>
        {value}
      </div>
    );
  }, [
    label,
    labelFormatter,
    payload,
    hideLabel,
    labelClassName,
    config,
    labelKey,
  ]);

  if (!active || !payload?.length) {
    return null;
  }

  const nestLabel = payload.length === 1 && indicator !== 'dot';

  return (
    <div
      className={cn(
        // When `borderColor` is supplied we swap the default 1px neutral
        // border for a 2px solid frame so the color reads clearly. The
        // border color itself is applied via inline style so it survives
        // any Tailwind class-order shake-out and stays one source of truth.
        'grid w-max min-w-[8rem] max-w-xs items-start gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-xs shadow-xl',
        borderColor ? 'border-solid border-2' : 'border border-neutral-200/50',
        className,
      )}
      style={borderColor ? { borderColor } : undefined}
    >
      {!nestLabel ? tooltipLabel : null}
      <div className='grid gap-1.5'>
        {payload
          .filter((item) => item.type !== 'none')
          .map((item, index) => {
            const key = `${nameKey ?? item.name ?? item.dataKey ?? 'value'}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);
            const indicatorColor =
              color ??
              getItemIndicatorColor?.(item) ??
              item.payload?.fill ??
              item.color;

            return (
              <div
                key={index}
                className={cn(
                  'flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-neutral-500',
                  indicator === 'dot' && 'items-center',
                )}
              >
                {formatter && item?.value !== undefined && item.name ? (
                  formatter(item.value, item.name, item, index, item.payload)
                ) : (
                  <>
                    {itemConfig?.icon ? (
                      <itemConfig.icon />
                    ) : (
                      !hideIndicator && (
                        <div
                          className={cn(
                            'shrink-0 rounded-[2px] border-[var(--color-border)] bg-[var(--color-bg)]',
                            {
                              'h-2.5 w-2.5': indicator === 'dot',
                              'w-1': indicator === 'line',
                              'w-0 border-[1.5px] border-dashed bg-transparent':
                                indicator === 'dashed',
                              'my-0.5': nestLabel && indicator === 'dashed',
                            },
                          )}
                          style={
                            {
                              '--color-bg': indicatorColor,
                              '--color-border': indicatorColor,
                            } as React.CSSProperties
                          }
                        />
                      )
                    )}
                    <div
                      className={cn(
                        'flex w-full min-w-0 flex-1 flex-col gap-1 leading-none',
                        'sm:flex-row sm:items-center sm:justify-between sm:gap-2',
                        nestLabel ? 'sm:items-end' : undefined,
                      )}
                    >
                      <div className='grid w-full min-w-0 max-w-full items-start gap-1.5 text-left sm:flex-1 sm:min-w-0'>
                        {nestLabel ? tooltipLabel : null}
                        <span className='min-w-0 break-words text-left text-neutral-500'>
                          {itemConfig?.label ?? item.name}
                        </span>
                      </div>
                      {item.value != null && (
                        <span className='w-full shrink-0 self-start text-left font-mono font-medium text-neutral-900 tabular-nums sm:w-auto sm:self-auto sm:pl-1 sm:text-right'>
                          {typeof item.value === 'number'
                            ? (() => {
                                const name =
                                  typeof item.name === 'string' ||
                                  typeof item.name === 'number'
                                    ? String(item.name)
                                    : String(item.dataKey ?? '');
                                if (rowValueFormatter) {
                                  try {
                                    return rowValueFormatter(
                                      item.value,
                                      name,
                                      (item.payload as Record<
                                        string,
                                        unknown
                                      >) ?? {},
                                    );
                                  } catch (err) {
                                    console.error(
                                      '[hen-ui] tooltipFormatter threw an error:',
                                      err,
                                    );
                                    return item.value.toLocaleString();
                                  }
                                }
                                return (
                                  valueFormatter?.(
                                    item.value,
                                    // Recharts' item.name is a union that can
                                    // include a non-string formatter fn; coerce
                                    // to primitive so our formatter signature
                                    // stays strict.
                                    typeof item.name === 'string' ||
                                      typeof item.name === 'number'
                                      ? item.name
                                      : String(item.dataKey ?? ''),
                                  ) ?? item.value.toLocaleString()
                                );
                              })()
                            : String(item.value)}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
      </div>
      {footer}
    </div>
  );
}

const ChartLegend = RechartsPrimitive.Legend;

function ChartLegendContent({
  className,
  hideIcon = false,
  payload,
  verticalAlign = 'bottom',
  nameKey,
}: React.ComponentProps<'div'> & {
  hideIcon?: boolean;
  nameKey?: string;
} & RechartsPrimitive.DefaultLegendContentProps) {
  const { config } = useChart();

  if (!payload?.length) {
    return null;
  }

  return (
    // Deviation from upstream shadcn: `flex-wrap` + `gap-y-2`. Prevents overflow,
    // on charts with many series, on a single row and clip swatches/labels at the
    // edges. Recharts re-measures the legend wrapper bbox and shrinks the plot
    // area on wrap, so this is safe for every chart that mounts the legend.
    <div
      className={cn(
        'flex flex-wrap items-center justify-center gap-x-4 gap-y-2',
        verticalAlign === 'top' ? 'pb-3' : 'pt-3',
        className,
      )}
    >
      {payload
        .filter((item) => item.type !== 'none')
        .map((item, index) => {
          const key = `${nameKey ?? item.dataKey ?? 'value'}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);

          return (
            // `min-w-0` lets the flex parent shrink an item below its content
            // width so a single long label (e.g. "Kinesiology / Exercise
            // Science") cannot push the wrap row past the card edge.
            <div
              key={index}
              className={cn(
                'flex min-w-0 items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-neutral-500',
              )}
            >
              {itemConfig?.icon && !hideIcon ? (
                <itemConfig.icon />
              ) : (
                <div
                  className='h-2 w-2 shrink-0 rounded-[2px]'
                  style={{
                    backgroundColor: item.color,
                  }}
                />
              )}
              {itemConfig?.label}
            </div>
          );
        })}
    </div>
  );
}

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: unknown,
  key: string,
) {
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }

  const payloadPayload =
    'payload' in payload &&
    typeof payload.payload === 'object' &&
    payload.payload !== null
      ? payload.payload
      : undefined;

  let configLabelKey: string = key;

  if (
    key in payload &&
    typeof payload[key as keyof typeof payload] === 'string'
  ) {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === 'string'
  ) {
    configLabelKey = payloadPayload[
      key as keyof typeof payloadPayload
    ] as string;
  }

  return configLabelKey in config ? config[configLabelKey] : config[key];
}

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
};
